import { useEffect, useState } from 'react';
import {
  Loader2,
  Clock,
  CheckCircle2,
  FolderOpen,
  FileText,
  GraduationCap,
  Award,
  ShieldCheck,
  Files,
} from 'lucide-react';

interface Props {
  /** True tant que la requête HTTP /validate tourne. Quand passe à false, le parent
   *  est censé fermer ce modal et ouvrir ValidationReceiptModal avec les résultats. */
  open: boolean;
}

/**
 * Modal de PROGRESSION affiché immédiatement au clic "Valider l'identité"
 * pendant que la requête HTTP /validate tourne (30-60s typiquement).
 *
 * Affiche les 6 étapes en checklist avec :
 *  - icône Clock grise = en attente
 *  - icône Loader2 animée = étape en cours (simulation visuelle, pas réelle)
 *  - icône CheckCircle2 verte = étape terminée
 *
 * Note importante : le backend n'expose pas (encore) le progrès réel des
 * étapes. On simule visuellement la progression avec un timer (1 étape /
 * ~5 sec) pour donner un retour visuel rassurant. Quand la vraie réponse
 * arrive, le parent ferme ce modal et ouvre ValidationReceiptModal qui
 * a les vrais statuts (✓ ou ❌) de chaque étape.
 *
 * Bug Rafik 28/05/2026 : "Quand on clique sur Valider, qu'il y ait un
 * compteur ou une barre de progression... à la fin, quelque chose de
 * terminé et fini. Ça évitera ces frustrations."
 */
export function ValidationProgressModal({ open }: Props) {
  // Index de l'étape "active" simulée (0 = première, 5 = dernière).
  // Toutes les étapes < currentStep sont vert (✓), step = bleu animé, > = gris.
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      return;
    }
    // Avance d'une étape toutes les 5 secondes, plafonne à la dernière étape.
    // Quand le vrai validate finit (parent ferme open), on revient à 0.
    const t = setInterval(() => {
      setCurrentStep((s) => (s < STEPS.length - 1 ? s + 1 : s));
    }, 5000);
    return () => clearInterval(t);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="bg-primary-50 flex items-center gap-3 border-b border-neutral-200 px-5 py-4">
          <Loader2 className="text-primary-600 h-5 w-5 animate-spin" />
          <div>
            <h2 className="text-primary-900 text-lg font-bold">Validation en cours…</h2>
            <p className="text-primary-700 text-xs">
              Création du dossier complet de l'apprenant — peut prendre 30 à 60 secondes
            </p>
          </div>
        </div>

        {/* Liste des étapes */}
        <div className="space-y-1 p-5">
          {STEPS.map((step, idx) => {
            const status: 'done' | 'current' | 'pending' =
              idx < currentStep ? 'done' : idx === currentStep ? 'current' : 'pending';
            return (
              <div
                key={step.id}
                className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                  status === 'done'
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : status === 'current'
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-neutral-200 bg-neutral-50/40'
                }`}
              >
                {/* Icône statut */}
                <div className="mt-0.5">
                  {status === 'done' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : status === 'current' ? (
                    <Loader2 className="text-primary-600 h-5 w-5 animate-spin" />
                  ) : (
                    <Clock className="h-5 w-5 text-neutral-300" />
                  )}
                </div>
                {/* Icône métier + description */}
                <div className="flex-1">
                  <div
                    className={`flex items-center gap-1.5 text-sm font-semibold ${
                      status === 'pending' ? 'text-neutral-400' : 'text-neutral-900'
                    }`}
                  >
                    {step.icon}
                    {step.label}
                  </div>
                  <div
                    className={`mt-0.5 text-[11px] ${
                      status === 'pending' ? 'text-neutral-400' : 'text-neutral-600'
                    }`}
                  >
                    {step.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-100 bg-neutral-50 px-5 py-3 text-[11px] italic text-neutral-500">
          ⏳ Merci de patienter… ne ferme pas la fenêtre. À la fin, le récap complet s'affichera
          avec les liens vers chaque élément créé.
        </div>
      </div>
    </div>
  );
}

const STEPS: { id: string; icon: React.ReactNode; label: string; desc: string }[] = [
  {
    id: 'drive',
    icon: <FolderOpen className="h-4 w-4 text-sky-600" />,
    label: 'Dossier Google Drive',
    desc: 'Création du dossier Exercice 2026 + 5 sous-dossiers (Convention, Attestations, Émargements, Certificat, Autres)',
  },
  {
    id: 'pdf',
    icon: <FileText className="h-4 w-4 text-violet-600" />,
    label: 'PDF EDOF source',
    desc: 'Upload du PDF EDOF original dans 01 - Convention (si fiche créée depuis un PDF)',
  },
  {
    id: 'docs',
    icon: <Files className="h-4 w-4 text-amber-600" />,
    label: '6 documents Google Docs',
    desc: 'Convocation + Welcome auto-remplis · Certificat + Attestation + Bilan + Rapport squelettes',
  },
  {
    id: 'moodle',
    icon: <GraduationCap className="h-4 w-4 text-emerald-600" />,
    label: 'Provisioning Moodle',
    desc: 'Création user Moodle + enrôlement automatique dans les cours liés à la formation',
  },
  {
    id: 'qualiopi',
    icon: <Award className="h-4 w-4 text-rose-600" />,
    label: 'Qualiopi F1 → F7',
    desc: '7 formulaires initialisés avec tokens HMAC (skippé en mode TEST pour éviter spam)',
  },
  {
    id: 'compliance',
    icon: <ShieldCheck className="h-4 w-4 text-emerald-600" />,
    label: 'Compliance Flag → Green',
    desc: 'Marquage Zoho "Audit Ready" + source = "Validé manuellement"',
  },
];
